import { VALIDATION_CONFIG } from '@constants/carFinance';
import getIndianCurrencyFormat from '@helpers/getIndianCurrencyFormat';
import type { ValidationResult } from '@interfaces/CarFinance.types';

export const validateDownPayment = (
  value: string,
  minDownPayment: number,
  maxDownPayment: number,
  carValue: number
): ValidationResult => {
  try {
    if (!value || typeof value !== 'string') {
      return { isValid: false, error: VALIDATION_CONFIG.downPayment.messages.invalidAmount };
    }

    // Allow digits, commas, and currency symbols for formatted input
    if (value && !/^[\d,₹\s]*$/.test(value)) {
      return { isValid: false, error: VALIDATION_CONFIG.downPayment.messages.invalidAmount };
    }

    const cleaned = value.replace(/[^\d]/g, "");
    const num = Number(cleaned);
    
    if (isNaN(num)) {
      return { isValid: false, error: VALIDATION_CONFIG.downPayment.messages.invalidNumber };
    }

    if (num < minDownPayment) {
      return { 
        isValid: false, 
        error: `Minimum down payment can be ₹${getIndianCurrencyFormat(minDownPayment)} (10% of car value)` 
      };
    } else if (num > maxDownPayment) {
      return { 
        isValid: false, 
        error: `Maximum down payment can be ₹${getIndianCurrencyFormat(maxDownPayment)} (80% of car value)` 
      };
    } else if (num > carValue) {
      return { 
        isValid: false, 
        error: VALIDATION_CONFIG.downPayment.messages.exceedsCarValue 
      };
    }

    return { isValid: true, error: "" };
  } catch (error) {
    console.error("Error validating down payment:", error);
    return { isValid: false, error: VALIDATION_CONFIG.downPayment.messages.invalidFormat };
  }
};

export const validateInterestRate = (value: string): ValidationResult => {
  try {
    if (value === "") {
      return { isValid: false, error: VALIDATION_CONFIG.interest.messages.enterInterest };
    }
    
    if (!/^\d*\.?\d*$/.test(value)) {
      return { isValid: false, error: VALIDATION_CONFIG.interest.messages.invalidFormat };
    }
    
    if ((value.match(/\./g) || []).length > 1) {
      return { isValid: false, error: VALIDATION_CONFIG.interest.messages.invalidDecimal };
    }
    
    if (value.includes('.') && value.split('.')[1]?.length > 2) {
      return { isValid: false, error: VALIDATION_CONFIG.interest.messages.maxDecimalPlaces };
    }
    
    const numVal = Number(value);
    
    if (isNaN(numVal)) {
      return { isValid: false, error: VALIDATION_CONFIG.interest.messages.invalidFormat };
    }
    
    if (value.endsWith(".")) {
      return { isValid: false, error: VALIDATION_CONFIG.interest.messages.completeDecimal };
    } else if (numVal < VALIDATION_CONFIG.interest.min) {
      return { isValid: false, error: VALIDATION_CONFIG.interest.messages.minInterest };
    } else if (numVal > VALIDATION_CONFIG.interest.max) {
      return { isValid: false, error: VALIDATION_CONFIG.interest.messages.maxInterest };
    }

    return { isValid: true, error: "" };
  } catch (error) {
    console.error("Error validating interest rate:", error);
    return { isValid: false, error: VALIDATION_CONFIG.interest.messages.invalidFormat };
  }
};
